import React, { useState, useEffect, useMemo } from 'react';
import apiService from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  ChevronDown,
  Search,
  Filter,
  LogIn,
  UserPlus,
  CreditCard,
  Globe,
  Database,
  Trash2,
  Plus,
  Pencil,
  AlertTriangle,
  Check,
  X,
  RefreshCw,
  Clock,
  Building2,
  Key,
  Mail,
  Zap,
  Package,
  FileText,
} from 'lucide-react';

interface AuditLog {
  id: number;
  action: string;
  user_id: number | null;
  workspace_id: number | null;
  resource_type: string;
  resource_id: string | null;
  details: any;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

interface AuditLogsProps {
  showAllWorkspaces?: boolean;
}

// Action category icons for visual scanning
const getActionIcon = (action: string): React.ElementType => {
  if (action.includes('login') || action.includes('logout')) return LogIn;
  if (action.includes('user') || action.includes('member') || action.includes('invite')) return UserPlus;
  if (action.includes('removed') || action.includes('deleted')) return Trash2;
  if (action.includes('subscription') || action.includes('payment') || action.includes('billing')) return CreditCard;
  if (action.includes('workspace') || action.includes('agency')) return Building2;
  if (action.includes('domain')) return Globe;
  if (action.includes('database')) return Database;
  if (action.includes('password') || action.includes('access')) return Key;
  if (action.includes('email')) return Mail;
  if (action.includes('lifetime') || action.includes('trial')) return Zap;
  if (action.includes('addon')) return Package;
  if (action.includes('updated') || action.includes('changed')) return Pencil;
  if (action.includes('created') || action.includes('added')) return Plus;
  return FileText;
};

const AuditLogs: React.FC<AuditLogsProps> = ({ showAllWorkspaces = false }) => {
const { agencyName } = useAuth();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [actorFilter, setActorFilter] = useState<string>('');
  const [targetFilter, setTargetFilter] = useState<string>('');

  // Dropdown states
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
  const [sourceDropdownOpen, setSourceDropdownOpen] = useState(false);

  // Input focus states
  const [actorFocused, setActorFocused] = useState(false);
  const [targetFocused, setTargetFocused] = useState(false);

  // Hover states for table rows
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/audit-logs');
setLogs((response.data || []).filter((log: AuditLog) => !log.details?.internal));
      setError(null);
    } catch (err: any) {
      console.error('Error fetching audit logs:', err);
      setError(err.response?.data?.error || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // Show relative time for recent events
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getActionLabel = (action: string) => {
    const labels: { [key: string]: string } = {
      login_success: 'Login Success',
      login_failed: 'Login Failed',
      logout: 'Logout',
      user_registered: 'User Registered',
      magic_link_requested: 'Magic Link Requested',
      magic_link_login: 'Magic Link Login',
      password_verified: 'Password Verified',
      profile_updated: 'Profile Updated',
      password_changed: 'Password Changed',
      email_changed: 'Email Changed',
      agency_updated: 'Agency Updated',
      agency_member_added: 'Member Added',
      agency_member_removed: 'Member Removed',
      agency_member_role_changed: 'Role Changed',
      member_invited: 'Member Invited',
      invite_viewed: 'Invite Viewed',
      invite_accepted: 'Invite Accepted',
      member_removed: 'Member Removed',
      member_role_changed: 'Role Changed',
      workspace_created: 'Workspace Created',
      workspace_updated: 'Workspace Updated',
      workspace_deleted: 'Workspace Deleted',
      workspace_member_added: 'Member Added',
      workspace_member_removed: 'Member Removed',
      workspace_member_role_changed: 'Role Changed',
      workspace_billing_profile_assigned: 'Billing Assigned',
      trial_started: 'Trial Started',
      trial_converted: 'Trial Converted',
      subscription_created: 'Subscription Created',
      subscription_changed: 'Subscription Changed',
      subscription_cancelled: 'Subscription Cancelled',
      subscription_reactivated: 'Subscription Reactivated',
      addon_added: 'Addon Added',
      addon_removed: 'Addon Removed',
      lifetime_deal_redeemed: 'Lifetime Redeemed',
      manual_payment_recorded: 'Payment Recorded',
      billing_profile_created: 'Billing Created',
      billing_profile_updated: 'Billing Updated',
      billing_profile_deleted: 'Billing Deleted',
      billing_profile_default_changed: 'Default Billing Changed',
      payment_method_added: 'Payment Added',
      payment_method_deleted: 'Payment Deleted',
      payment_method_default_changed: 'Default Payment Changed',
      payment_method_updated_webhook: 'Payment Updated',
      payment_succeeded: 'Payment Succeeded',
      payment_failed: 'Payment Failed',
      domain_added: 'Domain Added',
      domain_removed: 'Domain Removed',
      domain_dns_validated: 'Domain Validated',
      domain_validation_failed: 'Domain Failed',
      agency_portal_domain_added: 'Portal Domain Added',
      agency_portal_domain_removed: 'Portal Domain Removed',
      agency_portal_domain_validated: 'Portal Validated',
      agency_portal_domain_validation_failed: 'Portal Failed',
      agency_portal_domain_branding_updated: 'Portal Branding Updated',
      billing_access_granted: 'Access Granted',
      billing_access_revoked: 'Access Revoked',
      billing_access_checked: 'Access Checked',
      admin_password_reset_requested: 'Password Reset',
      admin_password_reset_completed: 'Reset Completed',
      admin_password_reset_failed: 'Reset Failed',
      admin_user_created: 'User Created',
      admin_user_creation_failed: 'Creation Failed',
      logs_deleted: 'Logs Deleted',
      logs_archived: 'Logs Archived',
      pods_restarted: 'Pods Restarted',
      failed_pods_cleaned: 'Pods Cleaned',
      database_credentials_accessed: 'DB Credentials Accessed',
      database_backed_up: 'DB Backed Up',
      database_reset: 'DB Reset',
      database_restored: 'DB Restored',
      database_restored_custom: 'DB Restored (Custom)',
      databases_backed_up_bulk: 'DBs Backed Up (Bulk)',
      orphan_databases_cleaned: 'Orphan DBs Cleaned',
      product_images_updated: 'Images Updated',
      customer_updated_webhook: 'Customer Updated',
    };
    return labels[action] || action.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getActionStatus = (action: string): 'success' | 'error' | 'warning' | 'info' => {
    if (action.includes('failed') || action.includes('removed') || action.includes('deleted') || action.includes('cancelled') || action.includes('revoked')) {
      return 'error';
    }
    if (action.includes('added') || action.includes('created') || action.includes('success') || action.includes('started') || action.includes('converted') || action.includes('redeemed') || action.includes('reactivated') || action.includes('granted') || action.includes('validated') || action.includes('succeeded') || action.includes('completed') || action.includes('accepted')) {
      return 'success';
    }
    if (action.includes('updated') || action.includes('changed')) {
      return 'warning';
    }
    return 'info';
  };

  const statusColors = {
    success: { dot: 'var(--v2-success)', text: 'var(--v2-success-dark)' },
    error: { dot: 'var(--v2-error)', text: '#991b1b' },
    warning: { dot: 'var(--v2-warning)', text: '#92400e' },
    info: { dot: 'var(--v2-primary)', text: 'var(--v2-primary)' },
  };

  // Filter logs
  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      if (actionFilter !== 'all' && log.action !== actionFilter) return false;
      if (sourceFilter !== 'all' && log.details?.source !== sourceFilter) return false;
if (actorFilter && !log.details?.actor_login?.toLowerCase().includes(actorFilter.toLowerCase()) && !log.details?.actor_email?.toLowerCase().includes(actorFilter.toLowerCase())) return false;
      if (targetFilter && !log.details?.target_name?.toLowerCase().includes(targetFilter.toLowerCase()) && !log.details?.target_email?.toLowerCase().includes(targetFilter.toLowerCase())) return false;
      return true;
    });
  }, [logs, actionFilter, sourceFilter, actorFilter, targetFilter]);

  // Dynamic filter options from data
  const uniqueActions = useMemo(() => Array.from(new Set(logs.map((l) => l.action))).sort(), [logs]);
  const uniqueSources = useMemo(() => Array.from(new Set(logs.map((l) => l.details?.source).filter(Boolean))).sort(), [logs]);

  // Active filter count
  const activeFilterCount = [actionFilter !== 'all', sourceFilter !== 'all', actorFilter !== '', targetFilter !== ''].filter(Boolean).length;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClick = () => {
      setActionDropdownOpen(false);
      setSourceDropdownOpen(false);
    };
    if (actionDropdownOpen || sourceDropdownOpen) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [actionDropdownOpen, sourceDropdownOpen]);

  const clearFilters = () => {
    setActionFilter('all');
    setSourceFilter('all');
    setActorFilter('');
    setTargetFilter('');
  };

const getSourceDisplayName = (source: string | null): string => {
    if (!source) return '—';
    if (source.startsWith('pod:')) return source.replace('pod:', '');
    if (source === 'management_ui') return agencyName ? `${agencyName} Platform` : 'Platform';
    if (source === 'api') return 'API';
    if (source === 'pod' || source === 'pod_widget') return 'Application';
    if (source.startsWith('worker:') || source === 'cli' || source === 'system') return 'System';
    return source;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <div
          style={{
            width: '32px',
            height: '32px',
            border: '2px solid var(--v2-border)',
            borderTopColor: 'var(--v2-primary)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          padding: '16px 20px',
          backgroundColor: 'var(--v2-error-bg)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid #fecaca',
        }}
      >
        <AlertTriangle size={18} strokeWidth={1.5} color="var(--v2-error)" style={{ flexShrink: 0, marginTop: '1px' }} />
        <span style={{ fontSize: '0.875rem', color: '#991b1b' }}>{error}</span>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'var(--font-sans)' }}>
      {/* Filters */}
      <div
        style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--v2-border)',
          padding: '20px',
          marginBottom: '20px',
        }}
      >
        {/* Filter Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Filter size={16} strokeWidth={1.5} color="var(--v2-text-muted)" />
            <span style={{ fontSize: '0.6875rem', fontWeight: 500, color: 'var(--v2-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Filters
            </span>
            {activeFilterCount > 0 && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '18px',
                  height: '18px',
                  padding: '0 6px',
                  backgroundColor: 'var(--v2-primary)',
                  color: '#ffffff',
                  borderRadius: 'var(--radius-full)',
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                }}
              >
                {activeFilterCount}
              </span>
            )}
          </div>
          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 10px',
                backgroundColor: 'transparent',
                color: 'var(--v2-text-tertiary)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.75rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'color 0.15s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--v2-text)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--v2-text-tertiary)')}
            >
              <X size={14} strokeWidth={1.5} />
              Clear all
            </button>
          )}
        </div>

        {/* Filter Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
          {/* Action Filter */}
          <div style={{ position: 'relative' }}>
            <label style={labelStyle}>Action</label>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActionDropdownOpen(!actionDropdownOpen);
                setSourceDropdownOpen(false);
              }}
              style={{
                ...dropdownButtonStyle,
                borderColor: actionDropdownOpen ? 'var(--v2-primary)' : 'var(--v2-border)',
                boxShadow: actionDropdownOpen ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
              }}
            >
              <span style={{ color: actionFilter === 'all' ? 'var(--v2-text-tertiary)' : 'var(--v2-text)' }}>
                {actionFilter === 'all' ? 'All actions' : getActionLabel(actionFilter)}
              </span>
              <ChevronDown
                size={16}
                strokeWidth={1.5}
                color="var(--v2-text-muted)"
                style={{ transition: 'transform 0.15s ease', transform: actionDropdownOpen ? 'rotate(180deg)' : 'rotate(0)' }}
              />
            </button>
            {actionDropdownOpen && (
              <div style={dropdownMenuStyle}>
                <DropdownItem selected={actionFilter === 'all'} onClick={() => { setActionFilter('all'); setActionDropdownOpen(false); }}>
                  All actions
                </DropdownItem>
                <div style={{ height: '1px', backgroundColor: 'var(--v2-border)', margin: '4px 0' }} />
                {uniqueActions.map((action) => {
                  const Icon = getActionIcon(action);
                  return (
                    <DropdownItem
                      key={action}
                      selected={actionFilter === action}
                      onClick={() => { setActionFilter(action); setActionDropdownOpen(false); }}
                      icon={<Icon size={14} strokeWidth={1.5} />}
                    >
                      {getActionLabel(action)}
                    </DropdownItem>
                  );
                })}
              </div>
            )}
          </div>

          {/* Source Filter */}
          <div style={{ position: 'relative' }}>
            <label style={labelStyle}>Source</label>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSourceDropdownOpen(!sourceDropdownOpen);
                setActionDropdownOpen(false);
              }}
              style={{
                ...dropdownButtonStyle,
                borderColor: sourceDropdownOpen ? 'var(--v2-primary)' : 'var(--v2-border)',
                boxShadow: sourceDropdownOpen ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
              }}
            >
              <span style={{ color: sourceFilter === 'all' ? 'var(--v2-text-tertiary)' : 'var(--v2-text)' }}>
                {sourceFilter === 'all' ? 'All sources' : sourceFilter}
              </span>
              <ChevronDown
                size={16}
                strokeWidth={1.5}
                color="var(--v2-text-muted)"
                style={{ transition: 'transform 0.15s ease', transform: sourceDropdownOpen ? 'rotate(180deg)' : 'rotate(0)' }}
              />
            </button>
            {sourceDropdownOpen && (
              <div style={dropdownMenuStyle}>
                <DropdownItem selected={sourceFilter === 'all'} onClick={() => { setSourceFilter('all'); setSourceDropdownOpen(false); }}>
                  All sources
                </DropdownItem>
                {uniqueSources.length > 0 && <div style={{ height: '1px', backgroundColor: 'var(--v2-border)', margin: '4px 0' }} />}
{uniqueSources.map((source) => (
                    <DropdownItem
                      key={source}
                      selected={sourceFilter === source}
                      onClick={() => { setSourceFilter(source); setSourceDropdownOpen(false); }}
                    >
                      {getSourceDisplayName(source)}
                    </DropdownItem>
                  ))}
              </div>
            )}
          </div>

          {/* Actor Filter */}
          <div>
            <label style={labelStyle}>Actor</label>
            <div style={{ position: 'relative' }}>
              <Search
                size={16}
                strokeWidth={1.5}
                color="var(--v2-text-muted)"
                style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}
              />
              <input
                type="text"
                value={actorFilter}
                onChange={(e) => setActorFilter(e.target.value)}
                onFocus={() => setActorFocused(true)}
                onBlur={() => setActorFocused(false)}
                placeholder="Search by email..."
                style={{
                  ...inputStyle,
                  paddingLeft: '36px',
                  borderColor: actorFocused ? 'var(--v2-primary)' : 'var(--v2-border)',
                  boxShadow: actorFocused ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
                }}
              />
            </div>
          </div>

          {/* Target Filter */}
          <div>
            <label style={labelStyle}>Target</label>
            <div style={{ position: 'relative' }}>
              <Search
                size={16}
                strokeWidth={1.5}
                color="var(--v2-text-muted)"
                style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}
              />
              <input
                type="text"
                value={targetFilter}
                onChange={(e) => setTargetFilter(e.target.value)}
                onFocus={() => setTargetFocused(true)}
                onBlur={() => setTargetFocused(false)}
                placeholder="Search target..."
                style={{
                  ...inputStyle,
                  paddingLeft: '36px',
                  borderColor: targetFocused ? 'var(--v2-primary)' : 'var(--v2-border)',
                  boxShadow: targetFocused ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)', margin: 0 }}>
          Showing <strong style={{ fontWeight: 600, color: 'var(--v2-text-secondary)' }}>{filteredLogs.length}</strong> of{' '}
          <strong style={{ fontWeight: 600, color: 'var(--v2-text-secondary)' }}>{logs.length}</strong> events
        </p>
        <button
          onClick={fetchAuditLogs}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 10px',
            backgroundColor: 'transparent',
            color: 'var(--v2-text-tertiary)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.75rem',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'color 0.15s ease',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--v2-primary)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--v2-text-tertiary)')}
        >
          <RefreshCw size={14} strokeWidth={1.5} />
          Refresh
        </button>
      </div>

      {/* Table */}
      <div
        style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--v2-border)',
          overflow: 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Timestamp</th>
              <th style={thStyle}>Action</th>
              <th style={thStyle}>Actor</th>
              <th style={thStyle}>Target</th>
              <th style={thStyle}>Source</th>
              <th style={thStyle}>IP Address</th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: '48px', textAlign: 'center' }}>
                  <FileText size={32} strokeWidth={1} color="var(--v2-text-muted)" style={{ marginBottom: '12px', opacity: 0.5 }} />
                  <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', margin: 0 }}>No audit logs found</p>
                  {activeFilterCount > 0 && (
                    <button
                      onClick={clearFilters}
                      style={{
                        marginTop: '12px',
                        padding: '8px 16px',
                        backgroundColor: 'var(--v2-surface)',
                        color: 'var(--v2-primary)',
                        border: '1px solid var(--v2-border)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      Clear filters
                    </button>
                  )}
                </td>
              </tr>
            ) : (
              filteredLogs.map((log) => {
                const status = getActionStatus(log.action);
                const colors = statusColors[status];
                const isHovered = hoveredRow === log.id;

                return (
                  <tr
                    key={log.id}
                    onMouseEnter={() => setHoveredRow(log.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={{
                      backgroundColor: isHovered ? 'var(--v2-surface-hover)' : 'transparent',
                      transition: 'background-color 0.1s ease',
                    }}
                  >
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Clock size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem', color: 'var(--v2-text-secondary)' }}>
                          {formatDate(log.created_at)}
                        </span>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: colors.dot,
                            flexShrink: 0,
                          }}
                        />
                        <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text)', fontWeight: 500 }}>
                          {getActionLabel(log.action)}
                        </span>
                      </div>
                    </td>
<td style={tdStyle}>
                      {log.details?.actor_login || log.details?.actor_email ? (
                        <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-secondary)' }}>
                          {[log.details.actor_login, log.details.actor_email].filter(Boolean).join(' / ')}
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-muted)' }}>—</span>
                      )}
                    </td>
<td style={tdStyle}>
                      {log.details?.target_name || log.details?.target_email ? (
                        <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-secondary)' }}>
                          {log.details.target_name || log.details.target_email}
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-muted)' }}>—</span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {log.details?.source ? (
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '3px 8px',
                            backgroundColor: 'var(--v2-surface-alt)',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: '0.75rem',
                            color: 'var(--v2-text-secondary)',
                            fontWeight: 500,
                          }}
                        >
{getSourceDisplayName(log.details?.source)}  
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-muted)' }}>—</span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {log.ip_address || log.details?.ip_address ? (
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>
                          {log.ip_address || log.details.ip_address}
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-muted)' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================
// STYLES
// ============================================

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.6875rem',
  fontWeight: 500,
  color: 'var(--v2-text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '6px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  backgroundColor: 'var(--v2-surface)',
  border: '1px solid var(--v2-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: '0.8125rem',
  color: 'var(--v2-text)',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
};

const dropdownButtonStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  backgroundColor: 'var(--v2-surface)',
  border: '1px solid var(--v2-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: '0.8125rem',
  color: 'var(--v2-text)',
  textAlign: 'left',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
};

const dropdownMenuStyle: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: '4px',
  backgroundColor: 'var(--v2-surface)',
  border: '1px solid var(--v2-border)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-lg)',
  zIndex: 20,
  maxHeight: '280px',
  overflowY: 'auto',
  padding: '4px',
};

const thStyle: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: '0.75rem',
  fontWeight: 500,
  color: 'var(--v2-text-muted)',
  borderBottom: '1px solid var(--v2-border)',
  backgroundColor: 'var(--v2-surface-alt)',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  borderBottom: '1px solid var(--v2-border-light)',
  verticalAlign: 'middle',
};

// ============================================
// DROPDOWN ITEM COMPONENT
// ============================================

const DropdownItem: React.FC<{
  children: React.ReactNode;
  selected: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
}> = ({ children, selected, onClick, icon }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 10px',
        fontSize: '0.8125rem',
        color: selected ? 'var(--v2-primary)' : 'var(--v2-text)',
        backgroundColor: selected ? 'var(--v2-primary-light)' : hovered ? 'var(--v2-surface-hover)' : 'transparent',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'background-color 0.1s ease',
        fontWeight: selected ? 500 : 400,
      }}
    >
      {icon && <span style={{ color: selected ? 'var(--v2-primary)' : 'var(--v2-text-tertiary)', flexShrink: 0 }}>{icon}</span>}
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children}</span>
      {selected && <Check size={14} strokeWidth={2} color="var(--v2-primary)" style={{ flexShrink: 0 }} />}
    </button>
  );
};

export default AuditLogs;
